import React from "react";
import {withTranslation} from "react-i18next";
import { useSelector } from "react-redux";
import classNames from "classnames";
import "./index.scss";

import { getExternalCycleLogo, getProviderById, getProviderNameById } from '../../../services/restaurant';

import { Avatar } from "../Avatar/Avatar";
import IconButton from '../IconButton/index.js';
import { declineDraftReason, getCurrencyIcons } from '../../../utils/enums.js';
import { fixedNumber } from '../../../utils/math-util.js';
import moment from 'moment';

const ProviderInfo = ({price, currency, deliveryPriceRatio, declineReason, onlyPrice, estimatedTimeToArrival}) => {
    return (
        <div className={'providerInfo'}>
            {!!declineReason && (
                <span className={'declineReason'}>{declineDraftReason()[declineReason]}</span>
            )}
            {!declineReason && (
                <>
                    {!onlyPrice && !!deliveryPriceRatio && (
                        <div className={'providerInfoItem'}>
                            <IconButton iconName={'product_percent'} isIcon/>
                            <span>{fixedNumber(deliveryPriceRatio * 100, 2)}%</span>
                        </div>
                    )}
                    {!!estimatedTimeToArrival && (
                        <div className={'providerInfoItem'}>
                            <IconButton iconName={'clock'} isIcon/>
                            <span>{moment(estimatedTimeToArrival).format("HH:mm")}</span>
                        </div>
                    )}
                    {!!price && (
                        <div className={'providerInfoItem'}>
                            <IconButton iconName={'coins'} isIcon/>
                            <span>{price} {getCurrencyIcons(currency)}</span>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

const GroupProvider = ({ draft, handleOnChecked, ordersInfo, checkedIds }) => {
    return (
        <div className="courierGroupItem group"
             onClick={e => handleOnChecked(draft, e)}
        >
            <div className={'courierGroupItemTop'}>
                <div className="courierInfo">
                    <div className={"courierInfoLeftSide"}>
                        <Avatar isObject={true} avatar={getExternalCycleLogo(getProviderById(draft.providerId))} color={''} />
                        <span className={'courierInfoHeader'}>
                            {getProviderNameById(draft.providerId)}
                        </span>
                    </div>
                    <ProviderInfo
                        estimatedTimeToArrival={draft.estimatedTimeToArrival}
                        price={draft.priceInfo?.price}
                        currency={draft.priceInfo?.currency}
                        declineReason={draft.declineReason}
                        deliveryPriceRatio={draft.deliveryPriceRatio}
                    />
                </div>
                {!draft.declineReason && (
                    <div className="courierCheck">
                        <input
                            type="checkbox"
                            id={`checkbox-${draft.name}`}
                            name="check"
                            checked={checkedIds.includes(draft.providerCycleId)}
                            onChange={e => handleOnChecked(draft, e)}
                        />
                        <label htmlFor={`checkbox-${draft.name}`} />
                    </div>
                )}
            </div>
            <div className={'courierGroupItemBottom'}>
                {draft?.orderIds?.map((orderId) => {
                    const order = ordersInfo?.[orderId] || {};
                    return (
                        <div className={'courierGroupProviderItem'} key={`${orderId}-${draft.providerCycleId}`}>
                            <div className={'courierGroupProviderItemInfo'}>
                                <div className='courierInfo'>
                                    {order.orderId ? `#${order.orderId}` : ''}
                                    <span className={'address'}>
                                        {order.address ? `${order.address?.city}, ${order.address?.street}` : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const AssociatePopupExternalProviders = ({
    t,
    checkedIds,
    handleChecked,
    providers,
    ordersInfo,
    emptyTitleText,
}) => {
    const isLoading = useSelector(state => state.sectionsWrapperData.isLoading);

    const handleOnChecked = (draft, e) => {
      if(e) {
        e.preventDefault();
      }

      if(!!draft.declineReason) {
        return;
      }

      if(checkedIds.includes(draft.providerCycleId)) {
        return handleChecked(checkedIds.filter(id => id !== draft.providerCycleId));
      }

      const currentDratIds = draft.orderIds;
      const { selectedOrderIdsByDraft, duplicatedIds} = checkedIds.reduce((acc, id) => {
        const find = providers.find(({providerCycleId}) => providerCycleId === id);
        const findSelectedIds = find?.orderIds || [];
        const duplicated = findSelectedIds.some(id => currentDratIds.includes(id));
        return {
          duplicatedIds: find && duplicated ? acc.duplicatedIds.concat(find.providerCycleId) : acc.duplicatedIds,
          selectedOrderIdsByDraft: acc.selectedOrderIdsByDraft.concat(find?.orderIds || []),
        }
      }, {
        duplicatedIds: [],
        selectedOrderIdsByDraft: []
      });


      if(selectedOrderIdsByDraft.some(id => draft.orderIds.includes(id)) && !duplicatedIds.length) {
        return;
      }

      handleChecked(checkedIds.filter(id => !duplicatedIds.includes(id)).concat(draft.providerCycleId));
    }

    return (
      <div className={classNames('associatePopupExternalProviders')}>
        <div className='headerGroup'>
          <span>{t('dashboard.couriersbar.EXTERNAL_PROVIDERS_GROUP_LABEL')}</span>
          {isLoading && <div className={'loader'} />}
        </div>
        {providers?.map((draft, index) =>
            <GroupProvider
              handleOnChecked={handleOnChecked}
              ordersInfo={ordersInfo}
              checkedIds={checkedIds}
              key={`${draft.provider}-${draft.name}-${draft.providerId}-${draft.providerCycleId}-${index}`}
              draft={draft}
            />
        )}
        {(!providers || !providers.length) &&
          emptyTitleText && (
            <div className="containerEmpty">
              <span className="empty">{emptyTitleText}</span>
            </div>
          )}
      </div>
    );
}

export default React.memo(withTranslation()(AssociatePopupExternalProviders));